'use client';

import Link from 'next/link';
import { AtSign, MailCheck, ShieldCheck, Sparkles, UserPlus, type LucideIcon } from 'lucide-react';
import { useAuthSession } from '@/components/rbac/AuthSessionProvider';
import { HomeGestionAtajos } from './HomeGestionAtajos';
import { HomePerfilEstado } from './HomePerfilEstado';
import { HomeSectionTitle } from './HomeSectionTitle';
import { Reveal } from './Reveal';

/**
 * Los tres pasos reales del alta, no una promesa de marketing: registro con el
 * correo institucional, el asistente de `completar-registro` (identidad,
 * nombre, datos académicos e identificadores externos) y la ficha ya publicada.
 * Si esos pasos cambian, esto cambia con ellos.
 */
const pasos: { titulo: string; detalle: string; icon: LucideIcon }[] = [
	{
		icon: MailCheck,
		titulo: 'Crea tu cuenta',
		detalle: 'Con tu correo institucional @uda.cl. Te llega un enlace de verificación.',
	},
	{
		icon: ShieldCheck,
		titulo: 'Completa tu perfil',
		detalle: 'Identidad, unidad, formación y tus identificadores ORCID o Scopus.',
	},
	{
		icon: Sparkles,
		titulo: 'Queda público',
		detalle: 'Tu ficha obtiene dirección propia y es encontrable en buscadores.',
	},
];

/**
 * Última banda del bloque de la portada, sobre blanco, justo antes del pie.
 * Tiene dos caras: para un visitante, la invitación a registrarse; para quien ya
 * entró, el estado de su propia ficha (`HomePerfilEstado`).
 *
 * Los pasos van EN COLUMNA a la derecha y no en una fila bajo el texto: con la
 * fila hacía falta un filete que la separara del encabezado, y ese filete
 * partía la banda en dos secciones que parecían no tener relación. En dos
 * columnas la relación es obvia —a la izquierda la invitación, a la derecha lo
 * que pasa si aceptás— y no hace falta ninguna línea.
 *
 * La columna izquierda es deliberadamente BREVE y más angosta que la derecha:
 * su trabajo es invitar y dar el botón, no explicar. Lo que explica son los
 * pasos; con un párrafo largo las dos mitades competían por la atención.
 *
 * Los números van unidos por una línea vertical: sin ella los tres bloques se
 * leen como opciones sueltas, y son una SECUENCIA. Es el mismo motivo por el
 * que están numerados.
 *
 * El estado por defecto —el del HTML servido, y el que ve un crawler— es el de
 * visitante, así que la invitación viaja SIEMPRE en el documento: la sesión solo
 * puede reemplazarla, nunca dejar el lugar vacío.
 */
export function HomeCta() {
	const { user, status } = useAuthSession();
	const authenticated = status === 'authenticated' && Boolean(user);

	// Con sesión la banda no desaparece: cambia de cara. A quien ya tiene cuenta
	// no se le ofrece crearla, pero el mismo lugar sirve para empujar lo que sí
	// le falta —completar su ficha—, que hoy es el problema real del portal.
	//
	// Sin slug (una cuenta de gestión sin investigador vinculado) no hay ficha
	// que medir, pero la banda TAMPOCO puede retirarse: dejaba la banda verde de
	// "qué encontrarás aquí" pegada al pie, también verde, y el bloque perdía su
	// respiro blanco. En su lugar va el panel de gestión.
	if (authenticated) {
		return user?.slug ? (
			<HomePerfilEstado slug={user.slug} />
		) : (
			<HomeGestionAtajos esAdmin={Boolean(user?.esAdmin || user?.role === 'admin')} />
		);
	}

	return (
		<div className="bg-white px-6 pb-20 pt-16 sm:px-12">
			<Reveal>
				<div className="grid gap-10 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-center lg:gap-16">
					<div>
						<HomeSectionTitle icon={UserPlus}>¿Investigas en la UDA?</HomeSectionTitle>
						<p className="mt-4 hidden text-[15px] leading-relaxed text-black/[0.66] sm:block">
							Crea tu perfil y publica tu trabajo.
						</p>
						<div className="mt-7 flex flex-nowrap items-center gap-3 sm:flex-wrap sm:gap-x-4 sm:gap-y-3">
							<Link
								href="/login?tab=registro"
								className="inline-flex shrink-0 items-center gap-2 rounded-[10px] bg-[#233a4d] px-4 py-2.5 text-[13px] font-semibold text-white transition hover:bg-[#233a4d]/90 sm:px-6 sm:py-3 sm:text-sm"
							>
								<UserPlus size={16} aria-hidden />
								Registrarse
							</Link>
							<p className="flex min-w-0 items-center gap-1.5 text-[11.5px] leading-tight text-slate-500 sm:gap-2 sm:text-[13px]">
								<AtSign size={14} strokeWidth={2} aria-hidden className="text-slate-400" />
								Solo correo institucional
							</p>
						</div>
					</div>

					{/* Los tres pasos responden la duda que sigue al botón —"¿y después
					    qué?"— sin obligar a entrar para averiguarlo. */}
					<ol className="relative flex flex-col gap-3">
						{pasos.map(({ titulo, detalle, icon: Icon }, index) => (
							<li
								key={titulo}
								className="relative flex items-start gap-4 rounded-[10px] border border-slate-200 bg-[#fafbfc] p-4"
							>
								{/* Conector al paso siguiente. Arranca bajo el número y baja hasta
								    el borde de la tarjeta más el hueco de 12px que las separa. */}
								{index < pasos.length - 1 && (
									<span
										aria-hidden
										className="absolute left-[35px] top-[54px] h-[calc(100%-38px)] w-px bg-slate-200"
									/>
								)}
								<span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-white text-[#354857] ring-1 ring-slate-200">
									<Icon size={18} strokeWidth={2} aria-hidden />
									<span className="absolute -right-1.5 -top-1.5 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#233a4d] text-[10px] font-bold text-white">
										{index + 1}
									</span>
								</span>
								<span className="min-w-0">
									<span className="block text-[14.5px] font-semibold text-slate-800">{titulo}</span>
									<span className="mt-1 block text-[13px] leading-relaxed text-slate-600">
										{detalle}
									</span>
								</span>
							</li>
						))}
					</ol>
				</div>
			</Reveal>
		</div>
	);
}
